import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnChanges,
	OnInit,
	OnDestroy,
	ChangeDetectorRef,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexibleSelectionTileComponent } from '@woolworthsnz/form';
import { FulfilmentWindowSummarySlot } from '@woolworthsnz/trader-api';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { displayDateAtServer } from '../../helpers';
import { FulfilmentService, FulfilmentState, FulfilmentStoreService } from '../../services';
import { Subject, takeUntil } from 'rxjs';

dayjs.extend(customParseFormat);

@Component({
	selector: 'fulfilment-flexible-delivery-slot-selection',
	templateUrl: './fulfilment-flexible-delivery-slot-selection.component.html',
	styleUrls: ['./fulfilment-flexible-delivery-slot-selection.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [FormsModule, ReactiveFormsModule, FlexibleSelectionTileComponent],
})
export class FulfilmentFlexibleDeliverySlotSelectionComponent implements OnChanges, OnInit, OnDestroy {
	@Input() slot?: FulfilmentWindowSummarySlot;
	@Input() controlName: string;
	@Input() date: string;
	@Input() formGroup: FormGroup;
	@Input() isSingleSlot: boolean;
	@Input() isFlexibleDeliveryV2: boolean;
	@Input() hasChevron: boolean;

	private destroyed$: Subject<boolean> = new Subject();

	ngOnChanges(): void {
		const selectedSlot = this.fulfilmentService.getFlexibleSelectedSlot();
		if (this.slot != null && this.slot.id === selectedSlot?.id) {
			this.updateForm();
		}
	}

	ngOnInit(): void {
		this.subscribeToFormChange();
		this.subscribeToStatusChange();
	}

	constructor(
		private fulfilmentStoreService: FulfilmentStoreService,
		private fulfilmentService: FulfilmentService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	private isSelectedSlotTheSameAsControl(id: number | undefined): boolean {
		if (id == null) {
			return false;
		}

		const slotName = this.formGroup.controls['slot']?.value;
		const slotId = this.getIdForSlot(slotName);
		return slotId == null || slotId === id;
	}

	private getIdForSlot(slotName: string): number | undefined {
		if (!slotName || !slotName.includes('~')) {
			return undefined;
		}
		return Number(slotName.split('~')[1]);
	}

	private subscribeToFormChange(): void {
		this.formGroup?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((v) => {
			if (this.slot != null && v.slot != null && !v.slot?.includes(this.slot.id)) {
				this.fulfilmentService.setFlexibleSelectedSlot(undefined);
				if (!this.isSingleSlot) {
					this.slot = undefined;
				}

				this.cdr.markForCheck();
			}
		});
	}

	private handleSingleSlot(allFlexibleDeliverySlots: FulfilmentWindowSummarySlot[] | undefined): void {
		if (!this.isFlexibleDeliveryV2 && allFlexibleDeliverySlots != null && allFlexibleDeliverySlots.length > 0) {
			// if MVP 1 get first flexible slot
			this.slot = allFlexibleDeliverySlots[0];
		}

		if (this.isFlexibleDeliveryV2 && allFlexibleDeliverySlots != null && allFlexibleDeliverySlots.length === 1) {
			this.slot = allFlexibleDeliverySlots[0];
		}
	}

	private handleMultipleSlots(allFlexibleDeliverySlots: FulfilmentWindowSummarySlot[] | undefined): void {
		this.slot =
			allFlexibleDeliverySlots?.find(
				(s: FulfilmentWindowSummarySlot) => s.status === FulfilmentWindowSummarySlot.StatusEnum.Selected
			) ?? this.slot;

		if (this.slot != null && this.isSelectedSlotTheSameAsControl(this.slot.id)) {
			this.updateForm();
		}

		if (
			this.slot != null &&
			!allFlexibleDeliverySlots?.find((s: FulfilmentWindowSummarySlot) => s.id === this.slot?.id)
		) {
			this.fulfilmentService.setFlexibleSelectedSlot(undefined);
			this.updateForm();
		}
	}

	private subscribeToStatusChange(): void {
		this.fulfilmentStoreService.state$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((fulfilmentState: FulfilmentState) => {
				this.slot = this.fulfilmentService.getFlexibleSelectedSlot();

				const allFlexibleDeliverySlots = fulfilmentState.slots?.filter(
					(s: FulfilmentWindowSummarySlot) => s.isFlexibleDelivery
				);

				if (this.isSingleSlot) {
					this.handleSingleSlot(allFlexibleDeliverySlots);
				} else {
					this.handleMultipleSlots(allFlexibleDeliverySlots);
				}
			});
	}

	get label(): string {
		if (this.slot == null && !this.isSingleSlot && this.isFlexibleDeliveryV2) {
			return 'Choose a 1hr window for delivery';
		}

		return `${displayDateAtServer(this.slot?.start)?.format('h:mma')} - ${displayDateAtServer(
			this.slot?.end
		)?.format('h:mma')}`;
	}

	updateForm(): void {
		if (this.slot && this.date) {
			this.formGroup.patchValue({
				slot: `${this.date}~${this.slot.id}~${this.slot.itemsLimit ?? ''}`,
			});
		} else {
			this.formGroup.patchValue({
				slot: null,
			});
		}
		this.cdr.markForCheck();
	}
}
